import {
  Button,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  Tooltip,
  Box,
  ButtonProps
} from '@mui/material'
import { Asset } from '../types'
import { useTranslation } from 'react-i18next'
import useDialog from '../hooks/useDialog'
import { usePolicy } from '../contexts/PolicyContext'
import { AssetStatus } from '../types/Asset'
import { Edit } from '@mui/icons-material'
import { MouseEvent } from 'react'
import AssetCompanyForm from './AssetCompanyForm'

export interface AssetCompanyEditButtonProps extends ButtonProps {
  asset: Asset
  component?: typeof IconButton | typeof Button
  afterUpdate?: (asset: Asset) => void
}

export default function AssetCompanyEditButton({
  asset,
  component = IconButton,
  afterUpdate,
  children,
  onClick,
  ...props
}: AssetCompanyEditButtonProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('asset')

  /**
   * The policy context.
   */
  const { isSuperadmin } = usePolicy()

  /**
   * The dialog state.
   */
  const { open, openDialog, closeDialog } = useDialog()

  /**
   * Handle the click event.
   */
  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    onClick?.(event)
    openDialog()
  }

  function handleAfterSubmit(asset: Asset) {
    afterUpdate?.(asset)
    closeDialog()
  }

  return (
    <>
      {asset.status !== AssetStatus.INACTIVE && isSuperadmin && (
        <>
          {component === IconButton ? (
            <Tooltip title={t('edit')}>
              <IconButton {...props} onClick={handleClick}>
                {children ?? <Edit />}
              </IconButton>
            </Tooltip>
          ) : (
            <Button {...props} onClick={handleClick}>
              {(children ?? asset?.companyId === null) ? t('addCompany') : t('editCompany')}
            </Button>
          )}
        </>
      )}

      <Dialog open={open} maxWidth="sm" fullWidth onClose={closeDialog}>
        <DialogTitle>{asset?.companyId === null ? t('addCompany') : t('editCompany')}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <AssetCompanyForm
              asset={asset}
              afterSubmit={handleAfterSubmit}
              onCancel={closeDialog}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  )
}
